/**
 * Acceso a datos.
 *
 * Una sola interfaz con dos implementaciones:
 *  - Firestore, cuando hay configuración (con su caché offline nativa).
 *  - Local (IndexedDB), para trabajar sin proyecto
 *    Firebase todavía. Los datos quedan en el dispositivo.
 *
 * La UI no sabe cuál está activa.
 */

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit as fsLimit,
  setDoc,
  updateDoc,
  writeBatch,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { db, COL, SIN_CONFIGURAR } from './firebase';
import {
  avisarCambio,
  escribir as escribirLocal,
  escucharCambios,
  contar,
  leerTodo,
  leerUno,
  migrarDesdeLocalStorage,
  vaciar,
} from './almacenLocal';
import type { DocGuia, DocCatalogo, DocItemContrato, DocCapitulo, DocProducto } from './tiposDatos';
import { metaNueva, uuidv7 } from './tiposDatos';

/** ¿Estamos contra Firestore o contra el almacén local? */
export const modoLocal = SIN_CONFIGURAR || !db;

// ── API genérica ──────────────────────────────────────────────────────────

/** Migración única desde el localStorage de la versión anterior. */
let migracion: Promise<number> | null = null;
function asegurarMigracion() {
  if (!migracion) migracion = migrarDesdeLocalStorage();
  return migracion;
}

export async function listar<T>(coleccion: string): Promise<T[]> {
  if (modoLocal) {
    await asegurarMigracion();
    return leerTodo<T>(coleccion);
  }
  const snap = await getDocs(collection(db!, coleccion));
  return snap.docs.map((d) => ({ ...(d.data() as T), id: d.id }));
}

/** Un documento cualquiera por identificador. */
export async function leerDoc<T>(coleccion: string, id: string): Promise<T | null> {
  if (modoLocal) {
    await asegurarMigracion();
    return (await leerUno<T>(coleccion, id)) ?? null;
  }
  const snap = await getDoc(doc(db!, coleccion, id));
  return snap.exists() ? { ...(snap.data() as T), id: snap.id } : null;
}

export async function guardar<T extends { id: string }>(
  coleccion: string,
  registro: T,
): Promise<void> {
  if (modoLocal) {
    await escribirLocal(coleccion, [registro]);
    avisarCambio(coleccion);
    return;
  }
  await setDoc(doc(db!, coleccion, registro.id), registro as Record<string, unknown>, {
    merge: true,
  });
}

export async function guardarLote<T extends { id: string }>(
  coleccion: string,
  registros: T[],
): Promise<void> {
  if (modoLocal) {
    // Se escribe por tandas para no armar una transacción enorme.
    for (let i = 0; i < registros.length; i += 1000) {
      await escribirLocal(coleccion, registros.slice(i, i + 1000));
    }
    avisarCambio(coleccion);
    return;
  }
  // Firestore admite 500 operaciones por lote.
  for (let i = 0; i < registros.length; i += 450) {
    const lote = writeBatch(db!);
    for (const r of registros.slice(i, i + 450)) {
      lote.set(doc(db!, coleccion, r.id), r as Record<string, unknown>, {
        merge: true,
      });
    }
    await lote.commit();
  }
}

export async function actualizar(
  coleccion: string,
  id: string,
  cambios: Record<string, unknown>,
  usuarioId: string | null,
): Promise<void> {
  const parche = {
    ...cambios,
    actualizadoEn: new Date().toISOString(),
    actualizadoPor: usuarioId,
  };
  if (modoLocal) {
    const actual = (await leerUno<Record<string, unknown>>(coleccion, id)) ?? {};
    await escribirLocal(coleccion, [
      {
        ...actual,
        id,
        ...parche,
        version: ((actual.version as number) ?? 1) + 1,
      },
    ]);
    avisarCambio(coleccion);
    return;
  }
  await updateDoc(doc(db!, coleccion, id), parche);
}

/** Suscripción a cambios. En modo local avisa a las demás pestañas. */
export function suscribir(coleccion: string, alCambiar: () => void): () => void {
  if (modoLocal) return escucharCambios(coleccion, alCambiar);
  return onSnapshot(collection(db!, coleccion), () => alCambiar());
}

// ── Guías ─────────────────────────────────────────────────────────────────

export interface FiltroGuias {
  desde?: string;
  hasta?: string;
  plantaCodigo?: string;
  clienteId?: string;
  estado?: string;
  maximo?: number;
}

export async function listarGuias(f: FiltroGuias = {}): Promise<DocGuia[]> {
  if (modoLocal) {
    let todas = (await listar<DocGuia>(COL.guias)).filter((g) => !g.eliminadoEn);
    if (f.desde) todas = todas.filter((g) => g.fecha >= f.desde!);
    if (f.hasta) todas = todas.filter((g) => g.fecha <= f.hasta!);
    if (f.plantaCodigo) todas = todas.filter((g) => g.plantaCodigo === f.plantaCodigo);
    if (f.clienteId) todas = todas.filter((g) => g.clienteId === f.clienteId);
    if (f.estado) todas = todas.filter((g) => g.estado === f.estado);
    todas.sort((a, b) =>
      a.fecha === b.fecha ? b.folio - a.folio : b.fecha.localeCompare(a.fecha),
    );
    return f.maximo ? todas.slice(0, f.maximo) : todas;
  }

  const cond = [];
  if (f.desde) cond.push(where('fecha', '>=', f.desde));
  if (f.hasta) cond.push(where('fecha', '<=', f.hasta));
  if (f.plantaCodigo) cond.push(where('plantaCodigo', '==', f.plantaCodigo));
  if (f.clienteId) cond.push(where('clienteId', '==', f.clienteId));
  if (f.estado) cond.push(where('estado', '==', f.estado));

  const q = query(
    collection(db!, COL.guias),
    ...cond,
    orderBy('fecha', 'desc'),
    ...(f.maximo ? [fsLimit(f.maximo)] : []),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...(d.data() as DocGuia), id: d.id }));
}

/**
 * Escucha las guías en vivo. Una sola suscripción para toda la app.
 *
 * Antes cada pantalla consultaba la colección entera y además se suscribía
 * para volver a consultarla ante cualquier cambio. Contra IndexedDB no se
 * notaba; contra Firestore, con 10.796 guías, eran más de 20.000 lecturas por
 * cada pantalla abierta. Aquí el propio snapshot trae los documentos: la
 * primera entrega cuesta la colección, y después sólo viaja lo que cambió.
 */
export function suscribirGuias(alRecibir: (guias: DocGuia[]) => void): () => void {
  const ordenar = (gs: DocGuia[]) =>
    gs.sort((a, b) => (a.fecha === b.fecha ? b.folio - a.folio : b.fecha.localeCompare(a.fecha)));

  if (modoLocal) {
    const cargar = async () => {
      await asegurarMigracion();
      alRecibir(ordenar((await leerTodo<DocGuia>(COL.guias)).filter((g) => !g.eliminadoEn)));
    };
    void cargar();
    return escucharCambios(COL.guias, () => void cargar());
  }

  return onSnapshot(collection(db!, COL.guias), (snap) => {
    alRecibir(
      ordenar(
        snap.docs
          .map((d) => ({ ...(d.data() as DocGuia), id: d.id }))
          .filter((g) => !g.eliminadoEn),
      ),
    );
  });
}

/** Una guía por identificador. */
export async function leerGuia(id: string): Promise<DocGuia | null> {
  if (modoLocal) return (await leerUno<DocGuia>(COL.guias, id)) ?? null;
  const snap = await getDoc(doc(db!, COL.guias, id));
  return snap.exists() ? { ...(snap.data() as DocGuia), id: snap.id } : null;
}

/**
 * ¿El folio ya existe en esa planta? Evita el duplicado antes de guardar.
 *
 * Va derecho al folio en vez de traerse la planta entera: es una lectura por
 * guardado, no cinco mil.
 */
export async function folioOcupado(
  plantaCodigo: string,
  folio: number,
  excluirId?: string,
): Promise<DocGuia | null> {
  if (modoLocal) {
    const guias = await listarGuias({ plantaCodigo });
    return (
      guias.find((g) => g.folio === folio && g.id !== excluirId && g.estado !== 'ANULADA') ?? null
    );
  }

  const snap = await getDocs(
    query(
      collection(db!, COL.guias),
      where('plantaCodigo', '==', plantaCodigo),
      where('folio', '==', folio),
      fsLimit(5),
    ),
  );
  return (
    snap.docs
      .map((d) => ({ ...(d.data() as DocGuia), id: d.id }))
      .find((g) => g.id !== excluirId && g.estado !== 'ANULADA' && !g.eliminadoEn) ?? null
  );
}

/**
 * Aplica los mismos cambios a muchas guías.
 *
 * No usa `guardarLote`: en modo local ese camino hace `put`, que reemplaza el
 * registro entero, y con un objeto parcial borraría la guía. Aquí se lee y se
 * fusiona; contra Firestore va como actualización por lotes.
 */
export async function marcarGuias(
  ids: string[],
  cambios: Partial<DocGuia>,
  usuarioId: string | null,
): Promise<number> {
  if (ids.length === 0) return 0;
  const parche = {
    ...cambios,
    actualizadoEn: new Date().toISOString(),
    actualizadoPor: usuarioId,
  };

  if (modoLocal) {
    const actuales = await Promise.all(ids.map((id) => leerUno<DocGuia>(COL.guias, id)));
    const fusionadas = actuales
      .filter((g): g is DocGuia => Boolean(g))
      .map((g) => ({ ...g, ...parche }));
    await escribirLocal(COL.guias, fusionadas);
    avisarCambio(COL.guias);
    return fusionadas.length;
  }

  for (let i = 0; i < ids.length; i += 450) {
    const lote = writeBatch(db!);
    for (const id of ids.slice(i, i + 450)) {
      lote.update(doc(db!, COL.guias, id), parche as Record<string, unknown>);
    }
    await lote.commit();
  }
  return ids.length;
}

/**
 * Borra todas las guías. Sin vuelta atrás.
 *
 * Existe para rehacer una carga desde cero cuando cambió el libro de origen.
 * En Firestore hay que leer los identificadores antes de borrarlos, así que
 * cuesta una lectura y un borrado por guía.
 */
export async function borrarTodasLasGuias(): Promise<number> {
  if (modoLocal) {
    const n = await contar(COL.guias);
    await vaciar(COL.guias);
    avisarCambio(COL.guias);
    return n;
  }

  let borradas = 0;
  for (;;) {
    const snap = await getDocs(query(collection(db!, COL.guias), fsLimit(450)));
    if (snap.empty) break;
    const lote = writeBatch(db!);
    for (const d of snap.docs) lote.delete(d.ref);
    await lote.commit();
    borradas += snap.size;
  }
  return borradas;
}

/** Último folio usado en la planta, para proponer el siguiente. */
export async function ultimoFolio(plantaCodigo: string): Promise<number | null> {
  const guias = await listarGuias({ plantaCodigo, maximo: 200 });
  if (!guias.length) return null;
  return Math.max(...guias.map((g) => g.folio));
}

export { uuidv7, metaNueva, Timestamp };
export type { DocGuia, DocCatalogo, DocItemContrato, DocCapitulo, DocProducto };
